import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { axiosInstance } from "../../services/axios";
var format = require("string-template");

export const useTableInfinity = (
  responseKey: string,
  url: string | undefined,
  tableRef: any,
  limit: number
) => {
  const [rows, setRows] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [lastFetchCount, setLastFetchCount] = useState(limit);
  const [isForbidden, setIsForbidden] = useState(false);
  const cancelSourceRef = useRef<any>(null);
  const [currentUrlInUse, setCurrentUrlInUse] = useState<any>(null);
  const didMount = useRef(false); // Track if the component has mounted
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Ref for debouncing

  
  const fetchNextPage = useCallback(async () => {
    if (!currentUrlInUse || isFetching) return;

    setIsFetching(true);

    const newCancelSource = axios.CancelToken.source();
    cancelSourceRef.current = newCancelSource;

    try {
      const response = await axiosInstance.get(
        format(currentUrlInUse, { offset: rows?.length || 0, limit }),
        { cancelToken: newCancelSource.token }
      );

      setRows((prevRows) => [...prevRows, ...(response?.data?.[responseKey] || [])]);
      setLastFetchCount(response?.data?.[responseKey]?.length || 0);
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request canceled:", error?.message);
      } else if (error?.response?.status === 403 ||error?.response?.status === 400||error?.response?.status === 404) {
        setIsForbidden(true);
      } else {
        console.log(error);
      }
    } finally {
      setIsFetching(false);
    }
  }, [rows, currentUrlInUse, limit, responseKey, isFetching]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (isForbidden || isFetching || lastFetchCount < limit) return;

      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

        if (scrollHeight - scrollTop - clientHeight < 400) {
          // Debounce calls to prevent rapid execution
          if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
          }

          debounceTimeoutRef.current = setTimeout(() => {
            fetchNextPage();
          }, 200); // Adjust debounce duration as needed
        }
      }
    },
    [fetchNextPage, isForbidden, isFetching, lastFetchCount, limit]
  );

  const resetAndFetch = useCallback(() => {
    if (!currentUrlInUse) return;

    // Cancel any ongoing requests
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel("Resetting the fetch process");
    }

    // Reset state and flags
    setRows([]);
    setIsForbidden(false);
    setLastFetchCount(limit);
    setIsFetching(false);

    // Trigger a fresh fetch
    // fetchNextPage();
  }, [currentUrlInUse, limit, fetchNextPage]);

  useEffect(() => {
    // Cancel ongoing requests when URL changes
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel("Previous request canceled due to URL change");
    }

    if (url) {
      setRows([]);
      setIsForbidden(false);
      setLastFetchCount(limit);
      didMount.current = false; // Reset the didMount flag on URL change
      setCurrentUrlInUse(url)
    }
  }, [url, limit]);

  useEffect(() => {
    if (!didMount.current && tableRef.current) {
      didMount.current = true; // Mark component as initialized
      fetchMoreOnBottomReached(tableRef.current); // Trigger first fetch
    }
  }, [fetchMoreOnBottomReached, tableRef]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current); // Cleanup debounce timer
      }
    };
  }, []);



  return { rows, setRows, fetchMoreOnBottomReached, isFetching, resetAndFetch };
};
